import React, { useState } from 'react';
import { Train, Book, FileText, Calendar, CheckCircle, Download, Users, Target, Award, TrendingUp, Clock, DollarSign, Briefcase, MapPin, GraduationCap, AlertCircle, BarChart, BookOpen, PenTool, Brain, Globe } from 'lucide-react';

const RRBNTPCPage = () => {
  const [selectedPost, setSelectedPost] = useState('Graduate');

  const postCategories = {
    'Graduate': {
      name: "Graduate Posts (Pay Level 6)",
      posts: [
        "Junior Account Assistant cum Typist",
        "Senior Clerk cum Typist",
        "Junior Time Keeper",
        "Trains Clerk",
        "Commercial cum Ticket Clerk"
      ],
      payLevel: "Pay Level 6 (₹35,400 - ₹1,12,400)",
      salary: "₹35,400 - ₹1,12,400",
      qualification: "Graduation in any discipline",
      icon: <GraduationCap />,
      color: "#667eea",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    },
    'Undergraduate': {
      name: "Undergraduate Posts (Pay Level 5)",
      posts: [
        "Commercial cum Ticket Clerk",
        "Accounts Clerk cum Typist",
        "Junior Clerk cum Typist",
        "Trains Clerk",
        "Commercial Apprentice"
      ],
      payLevel: "Pay Level 5 (₹29,200 - ₹92,300)",
      salary: "₹29,200 - ₹92,300",
      qualification: "12th Pass (10+2) from recognized board",
      icon: <BookOpen />,
      color: "#f093fb",
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
    },
    'Skilled': {
      name: "Skilled Posts (Pay Level 2 & 3)",
      posts: [
        "Station Master",
        "Goods Guard",
        "Senior Commercial cum Ticket Clerk",
        "Senior Clerk cum Typist",
        "Head Clerk"
      ],
      payLevel: "Pay Level 2-3 (₹19,900 - ₹81,100)",
      salary: "₹19,900 - ₹81,100",
      qualification: "10th + ITI or 12th Pass",
      icon: <Briefcase />,
      color: "#43e97b",
      gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
    }
  };

  const examPattern = {
    stage1: {
      name: "Stage 1: Computer Based Test (CBT-1)",
      duration: "90 Minutes",
      questions: 100,
      marks: 100,
      sections: [
        { subject: "General Awareness", questions: 40, marks: 40 },
        { subject: "Mathematics", questions: 30, marks: 30 },
        { subject: "General Intelligence & Reasoning", questions: 30, marks: 30 }
      ],
      negativeMarking: "⅓ mark deducted for each wrong answer"
    },
    stage2: {
      name: "Stage 2: Computer Based Test (CBT-2)",
      duration: "90 Minutes (Graduate) / 90 Minutes (Undergraduate)",
      questions: "120 (Graduate) / 120 (Undergraduate)",
      marks: "120 (Graduate) / 120 (Undergraduate)",
      sections: [
        { subject: "General Awareness", questions: 50, marks: 50 },
        { subject: "Mathematics", questions: 35, marks: 35 },
        { subject: "General Intelligence & Reasoning", questions: 35, marks: 35 }
      ],
      negativeMarking: "⅓ mark deducted for each wrong answer"
    },
    typing: {
      name: "Stage 3: Typing Skill Test / Computer Based Aptitude Test (CBAT)",
      description: "For specific posts requiring typing skills or aptitude",
      speed: "30 WPM in English or 25 WPM in Hindi",
      duration: "15 minutes"
    },
    document: {
      name: "Stage 4: Document Verification",
      description: "Final document verification and medical examination"
    }
  };

  const syllabus = {
    "General Awareness": [
      "Current Affairs: National & International",
      "Indian History: Ancient, Medieval, Modern",
      "Indian Polity & Constitution",
      "Indian Economy & Budget",
      "Geography: Indian & World",
      "General Science: Physics, Chemistry, Biology",
      "Sports: National & International",
      "Awards & Honours",
      "Books & Authors",
      "Important Days & Dates",
      "Indian Railways: History & Development",
      "Science & Technology Developments"
    ],
    "Mathematics": [
      "Number Systems & Simplification",
      "Decimals, Fractions & LCM HCF",
      "Ratio & Proportion",
      "Percentage & Averages",
      "Profit & Loss, Discount",
      "Simple & Compound Interest",
      "Time & Work, Time & Distance",
      "Mensuration: Area & Volume",
      "Algebra: Linear Equations",
      "Geometry: Lines, Angles, Triangles",
      "Trigonometry: Basic Concepts",
      "Statistics & Data Interpretation",
      "Probability"
    ],
    "General Intelligence & Reasoning": [
      "Analogies & Classification",
      "Series Completion: Number & Letter",
      "Coding-Decoding",
      "Blood Relations",
      "Direction Sense Test",
      "Syllogism & Statements",
      "Venn Diagrams",
      "Puzzle & Seating Arrangement",
      "Calendar & Clock",
      "Mathematical Operations",
      "Data Sufficiency",
      "Statement & Conclusions",
      "Logical Reasoning"
    ]
  };

  const importantDates = [
    { event: "Notification Release", date: "December - January", status: "Check Official Website" },
    { event: "Online Application Start", date: "January - February", status: "Apply Online" },
    { event: "Last Date to Apply", date: "February - March", status: "Don't Miss" },
    { event: "Admit Card CBT-1", date: "15 days before exam", status: "Download" },
    { event: "CBT-1 Examination", date: "March - June", status: "Tier 1" },
    { event: "CBT-1 Result", date: "2-3 months after exam", status: "Check Result" },
    { event: "CBT-2 Examination", date: "4-6 months after CBT-1", status: "Tier 2" },
    { event: "Typing/CBAT Test", date: "After CBT-2 Result", status: "Skill Test" },
    { event: "Final Result", date: "8-10 months from start", status: "Selection" }
  ];

  const eligibility = {
    ageLimit: {
      minimum: "18 years",
      maximum: "33 years (General), 36 years (OBC), 38 years (SC/ST)",
      relaxation: "Age relaxation as per government norms"
    },
    qualification: {
      graduate: "Bachelor's Degree in any discipline from recognized university",
      undergraduate: "10+2 or equivalent from recognized board",
      skilled: "10th + ITI or 12th Pass from recognized board"
    },
    applicationFee: {
      general: "₹500/-",
      obc: "₹500/-",
      scst: "₹250/-",
      female: "₹250/-",
      exServicemen: "Exempted"
    }
  };

  const benefits = [
    {
      icon: <DollarSign />,
      title: "Attractive Salary",
      desc: "Pay Level 2 to 6 with regular increments"
    },
    {
      icon: <Train />,
      title: "Free Rail Travel",
      desc: "Unlimited free travel for self and family across India"
    },
    {
      icon: <Award />,
      title: "Job Security",
      desc: "Lifetime job security under Government of India"
    },
    {
      icon: <Briefcase />,
      title: "Career Growth",
      desc: "Promotions based on experience and performance"
    },
    {
      icon: <MapPin />,
      title: "Posting Across India",
      desc: "Opportunity to work in different zones of Indian Railways"
    },
    {
      icon: <Users />,
      title: "Medical Benefits",
      desc: "Free medical facilities for employee and family"
    }
  ];

  const preparationStrategy = [
    {
      icon: <Book />,
      title: "Study NCERT Books",
      desc: "Focus on Class 6-10 NCERT for General Science and Mathematics"
    },
    {
      icon: <Globe />,
      title: "Current Affairs",
      desc: "Read newspapers daily and follow last 6 months current events"
    },
    {
      icon: <PenTool />,
      title: "Practice Mock Tests",
      desc: "Solve minimum 50+ full-length mock tests before exam"
    },
    {
      icon: <Brain />,
      title: "Speed & Accuracy",
      desc: "Focus on solving questions quickly with high accuracy"
    },
    {
      icon: <FileText />,
      title: "Previous Papers",
      desc: "Solve last 10 years RRB NTPC papers multiple times"
    },
    {
      icon: <Clock />,
      title: "Time Management",
      desc: "Practice completing 100 questions in 90 minutes"
    }
  ];

  const zones = [
    "Northern Railway (NR)",
    "North Eastern Railway (NER)",
    "Northeast Frontier Railway (NFR)",
    "Eastern Railway (ER)",
    "South Eastern Railway (SER)",
    "Southern Railway (SR)",
    "Central Railway (CR)",
    "Western Railway (WR)",
    "South Central Railway (SCR)",
    "South Western Railway (SWR)",
    "North Western Railway (NWR)",
    "West Central Railway (WCR)",
    "North Central Railway (NCR)",
    "South East Central Railway (SECR)",
    "East Central Railway (ECR)",
    "East Coast Railway (ECoR)",
    "Metro Railway, Kolkata"
  ];

  return (
    <div style={{ background: '#f8f9fa', minHeight: '100vh', marginTop:'20px' }}>
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #000000ff 0%, #3f007dff 50%, #007672ff 100%)',
        padding: '80px 20px 60px',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ fontSize: '4rem', marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
            <Train size={80} />
          </div>
          <h1 style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '20px' }}>
            RRB NTPC 2025
          </h1>
          <p style={{ fontSize: '1.4rem', marginBottom: '15px', opacity: 0.95 }}>
            Railway Recruitment Board - Non-Technical Popular Categories
          </p>
          <p style={{ fontSize: '1.2rem', marginBottom: '30px', opacity: 0.9 }}>
            35,000+ Vacancies | 17 Railway Zones | Lakhs of Aspirants
          </p>
          <div style={{
            background: 'rgba(255,255,255,0.2)',
            padding: '20px 25px',
            borderRadius: '10px',
            display: 'inline-block',
            marginBottom: '30px',
            maxWidth: '900px'
          }}>
            <p style={{ fontSize: '1.1rem', margin: 0, lineHeight: '1.6' }}>
              <strong>💼 Posts:</strong> Junior Accounts Assistant, Senior Clerk, Ticket Clerk, Station Master, Goods Guard & More<br/>
              <strong>💰 Salary Range:</strong> ₹19,900 - ₹1,12,400 per month + Allowances
            </p>
          </div>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => alert('Enrollment functionality')}
              style={{
                background: 'white',
                color: '#3f007df',
                padding: '15px 40px',
                borderRadius: '30px',
                border: 'none',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-3px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              Start RRB NTPC Preparation
            </button>
            <button
              onClick={() => document.getElementById('syllabus').scrollIntoView({ behavior: 'smooth' })}
              style={{
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                padding: '15px 40px',
                borderRadius: '30px',
                border: '2px solid white',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = '#667eea';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                e.currentTarget.style.color = 'white';
              }}
            >
              View Complete Syllabus
            </button>
          </div>
        </div>
      </div>

      {/* Post Category Tabs */}
      <div style={{ 
        background: 'white', 
        padding: '30px 20px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
        position: 'relative',
        top: '0',
        zIndex: 100
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px'
          }}>
            {Object.keys(postCategories).map(category => (
              <button
                key={category}
                onClick={() => setSelectedPost(category)}
                style={{
                  padding: '25px',
                  borderRadius: '12px',
                  border: 'none',
                  background: selectedPost === category 
                    ? postCategories[category].gradient
                    : '#f0f0f0',
                  color: selectedPost === category ? 'white' : '#333',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: selectedPost === category ? `0 4px 15px ${postCategories[category].color}40` : 'none',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  if (selectedPost !== category) {
                    e.currentTarget.style.background = '#e0e0e0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedPost !== category) {
                    e.currentTarget.style.background = '#f0f0f0';
                  }
                }}
              >
                <div style={{ fontSize: '2.5rem', marginBottom: '15px', display: 'flex' }}>
                  {postCategories[category].icon}
                </div>
                <div style={{ fontSize: '1.4rem', marginBottom: '8px' }}>
                  {postCategories[category].name}
                </div>
                <div style={{ fontSize: '0.95rem', opacity: selectedPost === category ? 0.9 : 0.7 }}>
                  {postCategories[category].salary}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' }}>
        
        {/* Post Details Section */}
        <section style={{ marginBottom: '60px' }}>
          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '20px',
            boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
            border: `3px solid ${postCategories[selectedPost].color}`
          }}>
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <div style={{ 
                fontSize: '3.5rem', 
                color: postCategories[selectedPost].color,
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'center'
              }}>
                {postCategories[selectedPost].icon}
              </div>
              
              <h2 style={{ 
                fontSize: '2.5rem', 
                fontWeight: 'bold', 
                marginBottom: '10px',
                color: '#2c3e50'
              }}>
                {postCategories[selectedPost].name}
              </h2>
              
              <p style={{ 
                fontSize: '1.3rem',
                color: '#666',
                marginBottom: '20px',
                fontWeight: '600'
              }}>
                {postCategories[selectedPost].payLevel}
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '25px',
              marginBottom: '30px'
            }}>
              <div style={{
                padding: '25px',
                background: `${postCategories[selectedPost].color}10`,
                borderRadius: '15px',
                border: `2px solid ${postCategories[selectedPost].color}30`
              }}>
                <h3 style={{ 
                  fontSize: '1.3rem', 
                  fontWeight: 'bold', 
                  marginBottom: '15px',
                  color: postCategories[selectedPost].color,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <Briefcase size={24} />
                  Available Posts
                </h3>
                <ul style={{ 
                  listStyle: 'none', 
                  padding: 0,
                  margin: 0
                }}>
                  {postCategories[selectedPost].posts.map((post, i) => (
                    <li key={i} style={{ 
                      padding: '10px 0',
                      borderBottom: i < postCategories[selectedPost].posts.length - 1 ? '1px solid #e0e0e0' : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      color: '#555'
                    }}>
                      <CheckCircle size={16} style={{ color: postCategories[selectedPost].color, flexShrink: 0 }} />
                      <span>{post}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div style={{
                padding: '25px',
                background: '#e3f2fd',
                borderRadius: '15px',
                border: '2px solid #2196f3'
              }}>
                <h3 style={{ 
                  fontSize: '1.3rem', 
                  fontWeight: 'bold', 
                  marginBottom: '15px',
                  color: '#1565c0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <GraduationCap size={24} />
                  Qualification Required
                </h3>
                <p style={{ 
                  color: '#555',
                  fontSize: '1.05rem',
                  lineHeight: '1.8',
                  margin: 0
                }}>
                  {postCategories[selectedPost].qualification}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            Why Join Indian Railways?
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px'
          }}>
            {benefits.map((benefit, idx) => (
              <div key={idx} style={{
                background: 'white',
                padding: '35px',
                borderRadius: '15px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                border: '2px solid transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-10px)';
                e.currentTarget.style.boxShadow = `0 12px 30px ${postCategories[selectedPost].color}30`;
                e.currentTarget.style.borderColor = postCategories[selectedPost].color;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
                e.currentTarget.style.borderColor = 'transparent';
              }}>
                <div style={{ 
                  fontSize: '3.5rem',
                  color: postCategories[selectedPost].color,
                  marginBottom: '20px',
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  {benefit.icon}
                </div>
                <h3 style={{ 
                  fontSize: '1.4rem',
                  fontWeight: 'bold',
                  marginBottom: '15px',
                  color: '#2c3e50'
                }}>
                  {benefit.title}
                </h3>
                <p style={{ 
                  color: '#666',
                  lineHeight: '1.6',
                  margin: 0,
                  fontSize: '1rem'
                }}>
                  {benefit.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Exam Pattern */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            RRB NTPC Exam Pattern 2025
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '30px'
          }}>
            {/* CBT-1 */}
            <div style={{
              background: 'white',
              padding: '35px',
              borderRadius: '15px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
              border: '2px solid #667eea'
            }}>
              <h3 style={{ 
                fontSize: '1.6rem',
                fontWeight: 'bold',
                marginBottom: '20px',
                color: '#667eea',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <Target size={28} />
                {examPattern.stage1.name}
              </h3>
              
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <span style={{ fontWeight: '600', color: '#2c3e50' }}>Duration:</span>
                  <span style={{ color: '#666' }}>{examPattern.stage1.duration}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <span style={{ fontWeight: '600', color: '#2c3e50' }}>Questions:</span>
                  <span style={{ color: '#666' }}>{examPattern.stage1.questions}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: '600', color: '#2c3e50' }}>Total Marks:</span>
                  <span style={{ color: '#666' }}>{examPattern.stage1.marks}</span>
                </div>
              </div>

              <div style={{
                background: '#f8f9fa',
                padding: '20px',
                borderRadius: '10px',
                marginBottom: '15px'
              }}>
                <h4 style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '15px', color: '#2c3e50' }}>
                  Section-wise Distribution
                </h4>
                {examPattern.stage1.sections.map((section, i) => (
                  <div key={i} style={{
                    padding: '10px 0',
                    borderBottom: i < examPattern.stage1.sections.length - 1 ? '1px solid #e0e0e0' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{ color: '#555', fontSize: '0.95rem' }}>{section.subject}</span>
                    <span style={{ fontWeight: '600', color: '#667eea' }}>{section.questions} Q | {section.marks} M</span>
                  </div>
                ))}
              </div>

              <div style={{
                background: '#fff3cd',
                padding: '15px',
                borderRadius: '8px',
                border: '1px solid #ffc107'
              }}>
                <p style={{ margin: 0, color: '#856404', fontSize: '0.9rem', fontWeight: '600' }}>
                  ⚠️ {examPattern.stage1.negativeMarking}
                </p>
              </div>
            </div>

            {/* CBT-2 */}
            <div style={{
              background: 'white',
              padding: '35px',
              borderRadius: '15px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
              border: '2px solid #f093fb'
            }}>
              <h3 style={{ 
                fontSize: '1.6rem',
                fontWeight: 'bold',
                marginBottom: '20px',
                color: '#f093fb',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <Award size={28} />
                {examPattern.stage2.name}
              </h3>
              
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <span style={{ fontWeight: '600', color: '#2c3e50' }}>Duration:</span>
                  <span style={{ color: '#666' }}>{examPattern.stage2.duration}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <span style={{ fontWeight: '600', color: '#2c3e50' }}>Questions:</span>
                  <span style={{ color: '#666' }}>{examPattern.stage2.questions}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: '600', color: '#2c3e50' }}>Total Marks:</span>
                  <span style={{ color: '#666' }}>{examPattern.stage2.marks}</span>
                </div>
              </div>

              <div style={{
                background: '#f8f9fa',
                padding: '20px',
                borderRadius: '10px',
                marginBottom: '15px'
              }}>
                <h4 style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '15px', color: '#2c3e50' }}>
                  Section-wise Distribution
                </h4>
                {examPattern.stage2.sections.map((section, i) => (
                  <div key={i} style={{
                    padding: '10px 0',
                    borderBottom: i < examPattern.stage2.sections.length - 1 ? '1px solid #e0e0e0' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{ color: '#555', fontSize: '0.95rem' }}>{section.subject}</span>
                    <span style={{ fontWeight: '600', color: '#f093fb' }}>{section.questions} Q | {section.marks} M</span>
                  </div>
                ))}
              </div>

              <div style={{
                background: '#fff3cd',
                padding: '15px',
                borderRadius: '8px',
                border: '1px solid #ffc107'
              }}>
                <p style={{ margin: 0, color: '#856404', fontSize: '0.9rem', fontWeight: '600' }}>
                  ⚠️ {examPattern.stage2.negativeMarking}
                </p>
              </div>
            </div>

            {/* Typing/CBAT Test */}
            <div style={{
              background: 'white',
              padding: '35px',
              borderRadius: '15px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
              border: '2px solid #43e97b'
            }}>
              <h3 style={{ 
                fontSize: '1.6rem',
                fontWeight: 'bold',
                marginBottom: '20px',
                color: '#43e97b',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <PenTool size={28} />
                {examPattern.typing.name}
              </h3>
              
              <p style={{ color: '#666', lineHeight: '1.8', marginBottom: '20px' }}>
                {examPattern.typing.description}
              </p>

              <div style={{
                background: '#f8f9fa',
                padding: '20px',
                borderRadius: '10px',
                marginBottom: '15px'
              }}>
                <div style={{ marginBottom: '15px' }}>
                  <span style={{ fontWeight: '600', color: '#2c3e50', display: 'block', marginBottom: '8px' }}>
                    Typing Speed Required:
                  </span>
                  <span style={{ color: '#666', fontSize: '0.95rem' }}>{examPattern.typing.speed}</span>
                </div>
                <div>
                  <span style={{ fontWeight: '600', color: '#2c3e50', display: 'block', marginBottom: '8px' }}>
                    Duration:
                  </span>
                  <span style={{ color: '#666', fontSize: '0.95rem' }}>{examPattern.typing.duration}</span>
                </div>
              </div>

              <div style={{
                background: '#e3f2fd',
                padding: '15px',
                borderRadius: '8px',
                border: '1px solid #2196f3'
              }}>
                <p style={{ margin: 0, color: '#1565c0', fontSize: '0.9rem', fontWeight: '600' }}>
                  📝 Only for posts requiring typing skills
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Syllabus Section */}
        <section id="syllabus" style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            RRB NTPC Complete Syllabus
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '25px'
          }}>
            {Object.entries(syllabus).map(([subject, topics], idx) => (
              <div key={idx} style={{
                background: 'white',
                padding: '30px',
                borderRadius: '15px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
                border: `2px solid ${postCategories[selectedPost].color}20`,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = postCategories[selectedPost].color;
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = `0 8px 25px ${postCategories[selectedPost].color}20`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = `${postCategories[selectedPost].color}20`;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
              }}>
                <h3 style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 'bold', 
                  marginBottom: '20px',
                  color: postCategories[selectedPost].color,
                  borderBottom: `3px solid ${postCategories[selectedPost].color}`,
                  paddingBottom: '10px'
                }}>
                  {subject}
                </h3>
                <ul style={{ 
                  listStyle: 'none', 
                  padding: 0,
                  margin: 0
                }}>
                  {topics.map((topic, i) => (
                    <li key={i} style={{ 
                      padding: '10px 0',
                      borderBottom: i < topics.length - 1 ? '1px solid #f0f0f0' : 'none',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      color: '#555',
                      fontSize: '0.95rem'
                    }}>
                      <CheckCircle size={16} style={{ color: postCategories[selectedPost].color, marginTop: '4px', flexShrink: 0 }} />
                      <span>{topic}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Eligibility Criteria */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            Eligibility Criteria
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '25px'
          }}>
            {/* Age Limit */}
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '15px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
              border: '2px solid #667eea'
            }}>
              <h3 style={{ 
                fontSize: '1.5rem',
                fontWeight: 'bold',
                marginBottom: '20px',
                color: '#667eea',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <Users size={24} />
                Age Limit
              </h3>
              <div style={{ marginBottom: '15px' }}>
                <div style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '8px' }}>Minimum Age:</div>
                <div style={{ color: '#666', fontSize: '1.05rem' }}>{eligibility.ageLimit.minimum}</div>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <div style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '8px' }}>Maximum Age:</div>
                <div style={{ color: '#666', fontSize: '1.05rem' }}>{eligibility.ageLimit.maximum}</div>
              </div>
              <div>
                <div style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '8px' }}>Relaxation:</div>
                <div style={{ color: '#666', fontSize: '1.05rem' }}>{eligibility.ageLimit.relaxation}</div>
              </div>
            </div>

            {/* Application Fee */}
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '15px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
              border: '2px solid #f093fb'
            }}>
              <h3 style={{ 
                fontSize: '1.5rem',
                fontWeight: 'bold',
                marginBottom: '20px',
                color: '#f093fb',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <DollarSign size={24} />
                Application Fee
              </h3>
              <div style={{
                background: '#f8f9fa',
                borderRadius: '10px',
                overflow: 'hidden'
              }}>
                {Object.entries(eligibility.applicationFee).map(([category, fee], i) => (
                  <div key={i} style={{
                    padding: '15px 20px',
                    borderBottom: i < Object.entries(eligibility.applicationFee).length - 1 ? '1px solid #e0e0e0' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{ color: '#555', textTransform: 'uppercase', fontSize: '0.9rem', fontWeight: '600' }}>
                      {category}
                    </span>
                    <span style={{ fontWeight: 'bold', color: '#f093fb', fontSize: '1.1rem' }}>
                      {fee}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Important Dates */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
          }}>
            <Calendar size={32} style={{ color: postCategories[selectedPost].color }} />
            Important Dates 2025
          </h2>

          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '15px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
          }}>
            <div style={{ overflow: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                fontSize: '1rem'
              }}>
                <thead>
                  <tr style={{ background: postCategories[selectedPost].gradient, color: 'white' }}>
                    <th style={{ padding: '15px', textAlign: 'left', borderRadius: '8px 0 0 0' }}>Event</th>
                    <th style={{ padding: '15px', textAlign: 'center' }}>Expected Date</th>
                    <th style={{ padding: '15px', textAlign: 'right', borderRadius: '0 8px 0 0' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {importantDates.map((item, idx) => (
                    <tr key={idx} style={{ 
                      background: idx % 2 === 0 ? '#f8f9fa' : 'white',
                      borderBottom: '1px solid #e0e0e0'
                    }}>
                      <td style={{ padding: '15px', fontWeight: '600', color: '#2c3e50' }}>
                        <Calendar size={16} style={{ display: 'inline', marginRight: '10px', color: postCategories[selectedPost].color }} />
                        {item.event}
                      </td>
                      <td style={{ padding: '15px', textAlign: 'center', color: '#666' }}>
                        {item.date}
                      </td>
                      <td style={{ padding: '15px', textAlign: 'right' }}>
                        <span style={{ 
                          background: postCategories[selectedPost].color + '20',
                          color: postCategories[selectedPost].color,
                          padding: '5px 15px',
                          borderRadius: '20px',
                          fontSize: '0.85rem',
                          fontWeight: '600'
                        }}>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Railway Zones */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
          }}>
            <MapPin size={32} style={{ color: postCategories[selectedPost].color }} />
            17 Railway Zones & RRBs
          </h2>

          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '15px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '15px'
            }}>
              {zones.map((zone, idx) => (
                <div key={idx} style={{
                  padding: '20px',
                  background: '#f8f9fa',
                  borderRadius: '10px',
                  border: '2px solid #e0e0e0',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = postCategories[selectedPost].color;
                  e.currentTarget.style.background = postCategories[selectedPost].color + '10';
                  e.currentTarget.style.transform = 'translateX(5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e0e0e0';
                  e.currentTarget.style.background = '#f8f9fa';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}>
                  <MapPin size={20} style={{ color: postCategories[selectedPost].color, flexShrink: 0 }} />
                  <span style={{ fontWeight: '600', color: '#2c3e50', fontSize: '0.95rem' }}>
                    {zone}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Preparation Strategy */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
          }}>
            <Target size={32} style={{ color: postCategories[selectedPost].color }} />
            Preparation Strategy
          </h2>

          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '15px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
          }}>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '25px'
            }}>
              {preparationStrategy.map((tip, idx) => (
                <div key={idx} style={{
                  padding: '25px',
                  border: '2px solid #e8e8e8',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = postCategories[selectedPost].color;
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = `0 8px 20px ${postCategories[selectedPost].color}20`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e8e8e8';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}>
                  <div style={{ 
                    fontSize: '2.5rem',
                    color: postCategories[selectedPost].color,
                    marginBottom: '15px',
                    display: 'flex'
                  }}>
                    {tip.icon}
                  </div>
                  <h4 style={{ 
                    fontSize: '1.3rem',
                    fontWeight: 'bold',
                    marginBottom: '10px',
                    color: '#2c3e50'
                  }}>
                    {tip.title}
                  </h4>
                  <p style={{ 
                    color: '#666',
                    lineHeight: '1.6',
                    margin: 0
                  }}>
                    {tip.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Course Packages */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
          }}>
            <Award size={32} style={{ color: postCategories[selectedPost].color }} />
            RRB NTPC Preparation Courses
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px'
          }}>
            {[
              {
                name: "Basic Course",
                price: "4,999",
                duration: "3 Months",
                features: [
                  "Complete Syllabus Coverage",
                  "Study Material (PDF)",
                  "15 Mock Tests",
                  "Video Lectures",
                  "Doubt Clearing Sessions"
                ],
                gradient: postCategories[selectedPost].gradient
              },
              {
                name: "Premium Course",
                price: "9,999",
                duration: "6 Months",
                features: [
                  "Everything in Basic",
                  "Live Interactive Classes",
                  "40+ Mock Tests",
                  "Previous Year Papers",
                  "Current Affairs (Daily)",
                  "Personal Mentorship",
                  "Typing Practice"
                ],
                gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                popular: true
              },
              {
                name: "Complete Package",
                price: "14,999",
                duration: "12 Months",
                features: [
                  "Everything in Premium",
                  "One-on-One Mentoring",
                  "Unlimited Mock Tests",
                  "Zone-wise Test Series",
                  "Interview Preparation",
                  "Performance Analytics",
                  "Study Planner",
                  "24/7 Support"
                ],
                gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
              }
            ].map((pkg, idx) => (
              <div key={idx} style={{
                background: 'white',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: pkg.popular ? '0 12px 40px rgba(102,126,234,0.3)' : '0 4px 15px rgba(0,0,0,0.08)',
                border: pkg.popular ? '3px solid #667eea' : '1px solid #e8e8e8',
                position: 'relative',
                transform: pkg.popular ? 'scale(1.05)' : 'scale(1)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = pkg.popular ? 'scale(1.08)' : 'scale(1.03)';
                e.currentTarget.style.boxShadow = '0 15px 45px rgba(102,126,234,0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = pkg.popular ? 'scale(1.05)' : 'scale(1)';
                e.currentTarget.style.boxShadow = pkg.popular ? '0 12px 40px rgba(102,126,234,0.3)' : '0 4px 15px rgba(0,0,0,0.08)';
              }}>
                {pkg.popular && (
                  <div style={{
                    position: 'absolute',
                    top: '20px',
                    right: '-35px',
                    background: '#FFD700',
                    color: '#333',
                    padding: '5px 40px',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    transform: 'rotate(45deg)',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                    zIndex: 1
                  }}>
                    MOST POPULAR
                  </div>
                )}
                
                <div style={{
                  background: pkg.gradient,
                  padding: '40px 30px',
                  textAlign: 'center',
                  color: 'white'
                }}>
                  <h3 style={{ 
                    fontSize: '1.8rem',
                    fontWeight: 'bold',
                    marginBottom: '15px'
                  }}>
                    {pkg.name}
                  </h3>
                  <div style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '10px' }}>
                    ₹{pkg.price}
                  </div>
                  <div style={{ fontSize: '1.1rem', opacity: 0.9 }}>
                    {pkg.duration}
                  </div>
                </div>

                <div style={{ padding: '30px' }}>
                  <ul style={{ 
                    listStyle: 'none',
                    padding: 0,
                    margin: '0 0 25px 0'
                  }}>
                    {pkg.features.map((feature, i) => (
                      <li key={i} style={{
                        padding: '12px 0',
                        borderBottom: i < pkg.features.length - 1 ? '1px solid #f0f0f0' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        color: '#555'
                      }}>
                        <CheckCircle size={16} style={{ color: '#43e97b', flexShrink: 0 }} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button style={{
                    width: '100%',
                    padding: '15px',
                    borderRadius: '10px',
                    border: 'none',
                    background: pkg.gradient,
                    color: 'white',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => alert('Enrollment functionality')}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}>
                    Enroll Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQs */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            Frequently Asked Questions
          </h2>

          <div style={{
            background: 'white',
            borderRadius: '15px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
            overflow: 'hidden'
          }}>
            {[
              {
                q: "What is RRB NTPC?",
                a: "RRB NTPC (Non-Technical Popular Categories) is a recruitment examination conducted by Railway Recruitment Boards for various non-technical posts in Indian Railways including clerks, ticket collectors, accounts assistants, and other administrative positions across 17 railway zones."
              },
              {
                q: "What is the age limit for RRB NTPC?",
                a: "The minimum age is 18 years and maximum age is 33 years for General category (as on January 1st of the year). Age relaxation of 3 years for OBC, 5 years for SC/ST, and as per government norms for other categories is provided."
              },
              {
                q: "What is the salary in RRB NTPC?",
                a: "Salary ranges from ₹19,900 to ₹1,12,400 per month depending on the post and pay level. Pay Level 2 (₹19,900-₹81,100), Pay Level 5 (₹29,200-₹92,300), and Pay Level 6 (₹35,400-₹1,12,400) are applicable for different posts."
              },
              {
                q: "Is there negative marking in RRB NTPC?",
                a: "Yes, there is negative marking of ⅓ (0.33) marks for each wrong answer in both CBT-1 and CBT-2 examinations. It's important to attempt questions carefully to avoid losing marks."
              },
              {
                q: "How many stages are there in RRB NTPC exam?",
                a: "RRB NTPC has 4 stages: Stage 1 - CBT-1 (Computer Based Test), Stage 2 - CBT-2 (Main Exam), Stage 3 - Typing Skill Test/CBAT (for specific posts), and Stage 4 - Document Verification and Medical Examination."
              },
              {
                q: "What is the exam pattern for RRB NTPC?",
                a: "CBT-1 has 100 questions (100 marks) in 90 minutes covering General Awareness (40Q), Mathematics (30Q), and Reasoning (30Q). CBT-2 has 120 questions (120 marks) in 90 minutes with similar sections but different weightage."
              },
              {
                q: "Can 12th pass apply for RRB NTPC?",
                a: "Yes, 12th pass candidates can apply for undergraduate level posts (Pay Level 5) in RRB NTPC. However, for graduate level posts (Pay Level 6), a bachelor's degree from a recognized university is required."
              },
              {
                q: "Which posts are available in RRB NTPC?",
                a: "Major posts include Junior Account Assistant, Senior Clerk, Commercial cum Ticket Clerk, Trains Clerk, Station Master, Goods Guard, Junior Time Keeper, and various other clerical and administrative positions across Indian Railways."
              },
              {
                q: "How to prepare for RRB NTPC in 3 months?",
                a: "Focus on NCERT books for basics, practice daily current affairs, solve previous year papers, take regular mock tests, improve speed and accuracy, revise important formulas, and maintain a consistent study schedule of 6-8 hours daily."
              },
              {
                q: "What are the benefits of working in Indian Railways?",
                a: "Benefits include attractive salary with regular increments, unlimited free rail travel for self and family, job security, pension benefits, medical facilities, housing allowances, promotion opportunities, and various other government perks and allowances."
              }
            ].map((faq, idx) => (
              <div key={idx} style={{
                padding: '30px',
                borderBottom: idx < 9 ? '1px solid #e8e8e8' : 'none'
              }}>
                <h4 style={{ 
                  fontSize: '1.3rem',
                  fontWeight: 'bold',
                  marginBottom: '15px',
                  color: postCategories[selectedPost].color,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px'
                }}>
                  <span style={{ flexShrink: 0 }}>Q{idx + 1}.</span>
                  <span>{faq.q}</span>
                </h4>
                <p style={{ 
                  color: '#666',
                  lineHeight: '1.8',
                  margin: '0 0 0 35px',
                  fontSize: '1.05rem'
                }}>
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section>
          <div style={{
            background: postCategories[selectedPost].gradient,
            padding: '60px 40px',
            borderRadius: '20px',
            textAlign: 'center',
            color: 'white'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
              <Train size={64} />
            </div>
            <h2 style={{ 
              fontSize: '2.5rem',
              fontWeight: 'bold',
              marginBottom: '20px'
            }}>
              Ready to Join Indian Railways?
            </h2>
            <p style={{ 
              fontSize: '1.3rem',
              marginBottom: '35px',
              opacity: 0.95,
              maxWidth: '700px',
              margin: '0 auto 35px'
            }}>
              Join thousands of successful candidates and secure your dream job in Indian Railways
            </p>
            <div style={{ 
              display: 'flex',
              gap: '20px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button 
                onClick={() => alert('Enrollment functionality')}
                style={{
                  background: 'white',
                  color: postCategories[selectedPost].color,
                  padding: '18px 45px',
                  borderRadius: '30px',
                  border: 'none',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.2)';
                }}>
                Start RRB NTPC Preparation
              </button>
              <button style={{
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                padding: '18px 45px',
                borderRadius: '30px',
                border: '2px solid white',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onClick={() => alert('Contact functionality')}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = postCategories[selectedPost].color;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                e.currentTarget.style.color = 'white';
              }}>
                Download Free Study Material
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* Footer Note */}
      <div style={{
        background: '#2c3e50',
        color: 'white',
        padding: '30px 20px',
        textAlign: 'center',
        marginTop: '60px'
      }}>
        <p style={{ margin: '0 0 10px', fontSize: '1.1rem' }}>
          © 2025 Olewin Academy - All Rights Reserved
        </p>
        <p style={{ margin: 0, opacity: 0.8 }}>
          For official RRB information, visit: <a href="https://www.rrbcdg.gov.in" target="_blank" rel="noopener noreferrer" style={{ color: '#43e97b', textDecoration: 'none' }}>RRB Official Portal</a>
        </p>
      </div>
    </div>
  );
};

export default RRBNTPCPage;